import { ChangeDetectionStrategy, Component, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AULAS_LIST } from '../data/course-data';

@Component({
  selector: 'app-aulas-section',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-10">
      <!-- Main Hub Header Banner -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-10 border border-white/10 shadow-2xl relative overflow-hidden">
        <!-- Background Gradient Glow -->
        <div class="absolute -top-24 -right-24 w-96 h-96 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

        <div class="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div class="space-y-3 max-w-3xl">
            <div class="flex items-center space-x-2">
              <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-semibold border border-[#F27D26]/30">
                <span class="material-symbols-outlined text-sm">school</span>
                <span>Menu de Aulas</span>
              </span>
              <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse mr-1.5"></span>
                Aulas 1, 2, 3, 4 e 5 Disponíveis
              </span>
            </div>

            <h2 class="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Aulas do Curso
            </h2>
            <p class="text-zinc-400 text-sm sm:text-base leading-relaxed">
              Central de aulas da disciplina de Empreendedorismo em Redes. Acesse os materiais práticos, dinâmicas e roteiros disponibilizados ao longo do semestre.
            </p>
          </div>

          <div class="bg-[#0A0A0B]/80 rounded-xl p-4 border border-white/10 w-full md:w-auto text-center md:text-left min-w-[180px]">
            <div class="text-2xl font-black text-[#F27D26]">Aulas 1 a 5</div>
            <div class="text-xs text-zinc-400 font-medium">Conteúdos Ativos</div>
          </div>
        </div>
      </div>

      <!-- Aulas Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        @for (aula of aulas; track aula.id) {
          <div class="bg-[#121214] border-[#F27D26]/40 hover:border-[#F27D26] hover:shadow-2xl hover:shadow-[#F27D26]/10 rounded-2xl p-6 border transition-all duration-300 flex flex-col justify-between space-y-6 relative overflow-hidden group">
            <div class="absolute top-0 right-0 w-32 h-32 bg-[#F27D26]/10 rounded-full blur-2xl pointer-events-none"></div>

            <div class="space-y-4">
              <!-- Top Row: Icon & Status -->
              <div class="flex items-center justify-between">
                <div class="w-12 h-12 rounded-xl bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/30 flex items-center justify-center font-bold">
                  <span class="material-symbols-outlined text-2xl">{{ aula.icon }}</span>
                </div>

                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping mr-1.5"></span>
                  Disponível
                </span>
              </div>

              <!-- Title & Subtitle -->
              <div>
                <div class="text-xs font-mono font-semibold text-[#F27D26] mb-1">
                  AULA {{ aula.number }} • {{ aula.duration }}
                </div>
                <h3 class="text-xl font-bold text-white group-hover:text-[#F27D26] transition-colors leading-snug">
                  {{ aula.title }}
                </h3>
                <p class="text-xs text-zinc-400 mt-2 leading-relaxed">
                  {{ aula.subtitle }}
                </p>
              </div>

              <!-- Topics List Preview -->
              @if (aula.topics && aula.topics.length > 0) {
                <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/5 space-y-2">
                  <div class="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider flex items-center space-x-1">
                    <span class="material-symbols-outlined text-xs text-[#F27D26]">checklist</span>
                    <span>Tópicos da Aula:</span>
                  </div>
                  <ul class="space-y-1">
                    @for (topic of aula.topics; track topic) {
                      <li class="text-xs text-zinc-400 flex items-start space-x-1.5">
                        <span class="text-[#F27D26] text-xs leading-none">•</span>
                        <span>{{ topic }}</span>
                      </li>
                    }
                  </ul>
                </div>
              }

              <!-- Tags -->
              <div class="flex flex-wrap gap-1.5 pt-1">
                @for (tag of aula.tags; track tag) {
                  <span class="px-2 py-0.5 rounded text-[10px] bg-white/5 text-zinc-300 border border-white/10 font-mono">
                    #{{ tag }}
                  </span>
                }
              </div>
            </div>

            <!-- Card Footer Action -->
            <div class="pt-4 border-t border-white/10 flex items-center justify-between">
              <span class="text-[11px] text-zinc-500 font-mono">
                {{ aula.sprintAssociated || 'Sprint 1' }}
              </span>

              <button
                type="button"
                (click)="onSelectAula(aula.routeId || 'aula1')"
                class="px-4 py-2 rounded-lg bg-[#F27D26] text-black font-bold text-xs hover:bg-[#e06c17] transition-all flex items-center space-x-1.5 shadow-lg shadow-[#F27D26]/20 cursor-pointer">
                <span>Acessar Aula 1</span>
                <span class="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>
          </div>
        }

        <!-- Placeholder Card for Future Lessons -->
        <div class="bg-[#121214]/50 border-dashed border border-white/15 rounded-2xl p-6 flex flex-col justify-between space-y-6">
          <div class="space-y-4">
            <div class="w-12 h-12 rounded-xl bg-white/5 text-zinc-500 border border-white/10 flex items-center justify-center font-bold">
              <span class="material-symbols-outlined text-2xl">update</span>
            </div>

            <div>
              <div class="text-xs font-mono font-semibold text-zinc-500 mb-1">
                PRÓXIMAS AULAS
              </div>
              <h3 class="text-lg font-bold text-zinc-400 leading-snug">
                Novas Aulas em Breve
              </h3>
              <p class="text-xs text-zinc-500 mt-2 leading-relaxed">
                As próximas aulas e materiais serão adicionados aqui conforme forem ministrados no decorrer do curso.
              </p>
            </div>
          </div>

          <div class="pt-4 border-t border-white/5 flex items-center justify-between text-xs text-zinc-600 font-mono">
            <span>Aguardando material</span>
            <span class="material-symbols-outlined text-base">hourglass_top</span>
          </div>
        </div>
      </div>
    </section>
  `
})
export class AulasSectionComponent {
  selectAula = output<string>();
  aulas = AULAS_LIST;

  onSelectAula(routeId: string) {
    this.selectAula.emit(routeId);
  }
}
